import React, { useState } from "react";

import {
  Avatar,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
  useToast
} from "@chakra-ui/react";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";

import { createGroup, updateGroup } from "~/repositories/groupRepository";
import type { Group } from "~/types/group";

interface GroupCardProps {
  initialEditMode?: boolean;
  initialGroup?: Group;
}

// Mock group data for new groups
const mockGroup: Omit<Group, "id" | "createdAt" | "updatedAt"> = {
  name: "",
  description: "",
  status: "active",
  profileImageUrl: "https://via.placeholder.com/150"
};

const GroupCard: React.FC<GroupCardProps> = ({ initialEditMode = false, initialGroup }) => {
  const [editMode, setEditMode] = useState(initialEditMode);
  const [formValues, setFormValues] = useState<Group | Omit<Group, "id" | "createdAt" | "updatedAt">>(
    initialGroup || mockGroup
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const inputBg = useColorModeValue("white", "gray.700");
  const inputReadOnlyBg = useColorModeValue("gray.50", "gray.700");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues.name) {
      errors.name = "Name is required";
    }

    if (!formValues.description) {
      errors.description = "Description is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      let savedGroup: Group;

      if ("id" in formValues) {
        // Update existing group
        savedGroup = await updateGroup(formValues as Group);
      } else {
        // Create new group
        savedGroup = await createGroup(formValues);
      }

      setFormValues(savedGroup);
      setEditMode(false);

      toast({
        title: "Group saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: "Error saving group",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const renderGroupForm = () => (
    <VStack spacing={4} align="stretch">
      <Flex justify="center" mb={4}>
        <Avatar size="xl" src={formValues.profileImageUrl} name={formValues.name} bg="blue.500" />
      </Flex>

      <FormControl isInvalid={!!formErrors.name}>
        <FormLabel color={labelColor}>Group Name</FormLabel>
        <Input
          name="name"
          value={formValues.name}
          onChange={handleInputChange}
          bg={inputBg}
          isReadOnly={!editMode}
          _readOnly={{ bg: inputReadOnlyBg }}
        />
        <FormErrorMessage>{formErrors.name}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!formErrors.description}>
        <FormLabel color={labelColor}>Description</FormLabel>
        <Input
          name="description"
          value={formValues.description}
          onChange={handleInputChange}
          bg={inputBg}
          isReadOnly={!editMode}
          _readOnly={{ bg: inputReadOnlyBg }}
        />
        <FormErrorMessage>{formErrors.description}</FormErrorMessage>
      </FormControl>

      {editMode && (
        <Flex justify="flex-end" gap={2}>
          <Button leftIcon={<FaTimes />} onClick={toggleEditMode} variant="outline">
            Cancel
          </Button>
          <Button leftIcon={<FaSave />} onClick={handleSave} colorScheme="blue" isLoading={isSubmitting}>
            Save
          </Button>
        </Flex>
      )}
    </VStack>
  );

  return (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" borderRadius="lg" boxShadow="md" maxW="600px">
      <CardBody>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Group Details
          </Text>
          {!editMode && (
            <Tooltip label="Edit Group">
              <IconButton aria-label="Edit group" icon={<FaEdit />} onClick={toggleEditMode} variant="ghost" />
            </Tooltip>
          )}
        </Flex>
        {renderGroupForm()}
      </CardBody>
    </Card>
  );
};

export default GroupCard;
